import { create } from 'zustand';

const useUIStore = create((set, get) => ({
  // Modals
  modals: {
    confirmDelete: {
      isOpen: false,
      title: '',
      message: '',
      onConfirm: null,
      onCancel: null
    },
    messagePreview: {
      isOpen: false,
      message: null
    },
    childDetails: {
      isOpen: false,
      child: null
    },
    qrScanner: {
      isOpen: false,
      onScan: null
    },
    imageUpload: {
      isOpen: false,
      onUpload: null,
      maxSize: 5242880 // 5MB
    }
  },

  // Toasts
  toasts: [],
  toastIdCounter: 0,

  // Loading states for specific operations
  loadingStates: {
    linkingChild: false,
    savingMessage: false,
    verifyingTelegram: false,
    uploadingPhoto: false,
    generatingQR: false
  },

  // Sidebar and navigation
  sidebarOpen: false,
  mobileMenuOpen: false,

  // Theme
  theme: 'light',

  // Global alerts
  globalAlert: null,

  // Actions for modals
  openModal: (modalName, props = {}) => {
    set((state) => ({
      modals: {
        ...state.modals,
        [modalName]: {
          ...state.modals[modalName],
          isOpen: true,
          ...props
        }
      }
    }));
  },

  closeModal: (modalName) => {
    set((state) => ({
      modals: {
        ...state.modals,
        [modalName]: {
          ...state.modals[modalName],
          isOpen: false
        }
      }
    }));
  },

  // Confirmation modal helper
  showConfirmation: (title, message, onConfirm, onCancel = null) => {
    set((state) => ({
      modals: {
        ...state.modals,
        confirmDelete: {
          isOpen: true,
          title,
          message,
          onConfirm,
          onCancel
        }
      }
    }));
  },

  // Actions for toasts
  showToast: (message, type = 'info', duration = 5000, action = null) => {
    const { toastIdCounter } = get();
    const id = toastIdCounter + 1;

    const toast = {
      id,
      message,
      type, // 'success', 'error', 'warning', 'info'
      duration,
      action, // { label: 'Undo', onClick: () => {} }
      createdAt: Date.now()
    };

    set((state) => ({
      toasts: [...state.toasts, toast],
      toastIdCounter: id
    }));

    // Auto-remove toast after duration
    if (duration > 0) {
      setTimeout(() => {
        get().removeToast(id);
      }, duration);
    }

    return id;
  },

  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter(t => t.id !== id)
    }));
  },

  clearToasts: () => {
    set({ toasts: [] });
  },

  // Quick toast helpers
  showSuccess: (message, duration = 5000) => {
    return get().showToast(message, 'success', duration);
  },

  showError: (message, duration = 7000) => {
    return get().showToast(message, 'error', duration);
  },

  showWarning: (message, duration = 6000) => {
    return get().showToast(message, 'warning', duration);
  },

  showInfo: (message, duration = 5000) => {
    return get().showToast(message, 'info', duration);
  },

  // Loading state management
  setLoading: (operation, isLoading) => {
    set((state) => ({
      loadingStates: {
        ...state.loadingStates,
        [operation]: isLoading
      }
    }));
  },

  isLoading: (operation) => {
    return get().loadingStates[operation] || false;
  },

  // Sidebar actions
  toggleSidebar: () => {
    set((state) => ({ sidebarOpen: !state.sidebarOpen }));
  },

  setSidebarOpen: (isOpen) => {
    set({ sidebarOpen: isOpen });
  },

  // Mobile menu actions
  toggleMobileMenu: () => {
    set((state) => ({ mobileMenuOpen: !state.mobileMenuOpen }));
  },

  setMobileMenuOpen: (isOpen) => {
    set({ mobileMenuOpen: isOpen });
  },

  // Theme actions
  setTheme: (theme) => {
    set({ theme });
    // Update document attribute for DaisyUI
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  },

  toggleTheme: () => {
    const { theme } = get();
    const newTheme = theme === 'light' ? 'dark' : 'light';
    get().setTheme(newTheme);
  },

  // Global alert
  setGlobalAlert: (alert) => {
    set({ globalAlert: alert });
  },

  clearGlobalAlert: () => {
    set({ globalAlert: null });
  },

  // Celebration animation trigger
  celebrate: (type = 'confetti') => {
    // This can trigger a global celebration animation
    // Implementation depends on the animation library used
    const event = new CustomEvent('celebrate', { detail: { type } });
    window.dispatchEvent(event);
  },

  // Progress indicator for multi-step processes
  progressIndicator: {
    visible: false,
    progress: 0,
    message: ''
  },

  showProgress: (progress, message = '') => {
    set({
      progressIndicator: {
        visible: true,
        progress,
        message
      }
    });
  },

  hideProgress: () => {
    set({
      progressIndicator: {
        visible: false,
        progress: 0,
        message: ''
      }
    });
  },

  // Initialize UI preferences from localStorage
  initialize: () => {
    // Load theme from localStorage
    const savedTheme = localStorage.getItem('theme') || 'light';
    get().setTheme(savedTheme);

    // Load other preferences...
  },

  // Reset UI state
  reset: () => {
    set({
      modals: {
        confirmDelete: {
          isOpen: false,
          title: '',
          message: '',
          onConfirm: null,
          onCancel: null
        },
        messagePreview: {
          isOpen: false,
          message: null
        },
        childDetails: {
          isOpen: false,
          child: null
        },
        qrScanner: {
          isOpen: false,
          onScan: null
        },
        imageUpload: {
          isOpen: false,
          onUpload: null,
          maxSize: 5242880
        }
      },
      toasts: [],
      loadingStates: {
        linkingChild: false,
        savingMessage: false,
        verifyingTelegram: false,
        uploadingPhoto: false,
        generatingQR: false
      },
      sidebarOpen: false,
      mobileMenuOpen: false,
      globalAlert: null,
      progressIndicator: {
        visible: false,
        progress: 0,
        message: ''
      }
    });
  }
}));

// Initialize on app load
if (typeof window !== 'undefined') {
  useUIStore.getState().initialize();
}

export default useUIStore;